/**
 * Alpine components for the call surfaces:
 *   - incomingCallModal: the global ring modal (Accept / Decline).
 *   - inCallOverlay: the full-screen in-call surface (audio card or video),
 *     and the after-call screen that follows a connected call.
 *   - threadCallButtons: the PM thread-header call affordance (button matrix).
 *
 * All three subscribe to the single call-state machine and call its methods.
 * Track attachment lives here because the overlay owns the <video>/<audio> refs.
 */

import { callState, hasAfterCallScreen, PEER_DISCONNECT_GRACE_MS } from './call-state.js';
import { callApi, CallApiError } from './call-api.js';
import { followWebPhoneGateRefusal } from '../../modules/phone-gate-refusal.js';
import { siteLocale } from '../../modules/site-locale.js';

const CALL_ACTIVE_STATUSES = ['ringing-out', 'connecting', 'active'];

// Values accepted by POST /v1/call/{id}/feedback. Order is the display order.
const RATING_REASONS = [
    'cant_hear_them',
    'they_cant_hear_me',
    'video_froze_or_blurry',
    'call_dropped',
    'echo_or_noise',
];
const VIDEO_ONLY_REASONS = ['video_froze_or_blurry'];
const AFTER_CALL_AUTO_CLOSE_SECONDS = 8;

// Chrome lists these aliases beside the physical devices they point at.
const OUTPUT_ALIAS_IDS = ['default', 'communications'];

// A name interpolated into a sentence is cut to this many characters.
const SHORT_NAME_LENGTH = 24;

function t(key, params) {
    return window.t ? window.t(key, params) : key;
}

function lucide(el) {
    if (window.LucideIcons) {
        window.LucideIcons.initialize(el);
    }
}

export function shortName(name) {
    const chars = Array.from(name || '');
    return chars.length > SHORT_NAME_LENGTH
        ? chars.slice(0, SHORT_NAME_LENGTH - 1).join('') + '…'
        : chars.join('');
}

function overlayRoot() {
    return document.querySelector('[data-call-surface="overlay"]');
}

// The page element that had focus when the ring arrived. The overlay adopts it
// when the ring is answered, so the end of the call returns focus there.
let ringFocusReturn = null;

function pageFocus() {
    const el = document.activeElement;
    return el && el !== document.body && !el.closest('[data-call-surface]') ? el : null;
}

function focusIfShown(el) {
    if (el && el.isConnected && el.offsetParent !== null) {
        el.focus();
    }
}

function isTransientFailure(e) {
    return !(e instanceof CallApiError) || e.status === 0 || e.status >= 500;
}

// A dialog stacked above the overlay owns the keyboard and the focus.
function stackedDialogOpen() {
    return !!window.ConfirmModal?.activeModal || callState.snapshot().status === 'ringing-in';
}

function formatDuration(totalSeconds) {
    const total = Math.max(0, Math.round(Number(totalSeconds) || 0));
    const minutes = Math.floor(total / 60);
    const seconds = String(total % 60).padStart(2, '0');
    return `${minutes}:${seconds}`;
}

const SERVICE_UNAVAILABLE_CODES = ['calls_disabled', 'call_unconfigured'];

// The per-person backoff lasts one minute. A longer retry_after is the daily call cap.
const RATE_LIMIT_BACKOFF_SECONDS = 60;

// "in 5 hours" in the page language, rounded up to whole minutes or hours.
function retryTime(seconds) {
    const format = new Intl.RelativeTimeFormat(siteLocale(), { numeric: 'always' });
    return seconds < 3600
        ? format.format(Math.ceil(seconds / 60), 'minute')
        : format.format(Math.ceil(seconds / 3600), 'hour');
}

let afterCallOpen = false;

export function afterCallScreenOpen() {
    return afterCallOpen;
}

/**
 * The user-facing sentence for a call denial code, from POST /v1/call,
 * POST /v1/call-authorization or GET /v1/call-authorization's
 * call_unavailable_reason. A code with no copy names the person as unavailable.
 * Only a failure with no code (network, parse) takes the generic sentence.
 */
function callErrorMessage(code, name, retryAfter = 0) {
    const params = { username: shortName(name) };
    if (code === 'call_rate_limited' && retryAfter > RATE_LIMIT_BACKOFF_SECONDS) {
        return t('common.calls_error_call_daily_limit', { time: retryTime(retryAfter) });
    }
    if (SERVICE_UNAVAILABLE_CODES.includes(code)) {
        return t('common.calls_toast_unavailable');
    }
    if (!code) {
        return t('common.calls_error_unavailable', params);
    }
    const key = `common.calls_error_${code}`;
    const translated = t(key, params);
    return translated !== key ? translated : t('common.calls_error_call_recipient_unavailable', params);
}

export function incomingCallModal() {
    return {
        visible: false,
        callId: '',
        type: 'audio',
        otherName: '',
        otherUsername: '',
        otherAvatar: '',
        _unsub: null,

        init() {
            this._unsub = callState.subscribe((s) => this.render(s));
            this.render(callState.snapshot());
        },

        destroy() {
            if (this._unsub) {
                this._unsub();
            }
        },

        render(s) {
            if (s.status === 'ringing-in') {
                this.callId = s.callId || '';
                this.type = s.type || 'audio';
                this.otherName = s.other?.name || s.other?.username || '';
                this.otherUsername = s.other?.username || '';
                this.otherAvatar = s.other?.avatar || '';
                if (!this.visible) {
                    ringFocusReturn = pageFocus() || ringFocusReturn;
                    this.visible = true;
                    this.$nextTick(() => {
                        lucide(this.$el);
                        // The panel takes focus, not Accept, so a stray Enter cannot answer.
                        this.$refs.panel?.focus();
                    });
                }
            } else if (this.visible) {
                this.visible = false;
                if (!CALL_ACTIVE_STATUSES.includes(s.status)) {
                    const el = ringFocusReturn;
                    ringFocusReturn = null;
                    focusIfShown(el);
                }
            }
        },

        isVideo() {
            return this.type === 'video';
        },

        accept() {
            callState.accept();
        },

        decline() {
            callState.decline();
        },
    };
}

export function inCallOverlay() {
    return {
        visible: false,
        // 'call' while a call runs, 'after' for the after-call screen.
        phase: 'call',
        status: 'idle',
        type: 'audio',
        role: 'caller',
        otherId: '',
        otherName: '',
        otherUsername: '',
        otherAvatar: '',
        muted: false,
        cameraOff: false,
        reconnecting: false,
        peerCameraOff: false,
        peerMicMuted: false,
        weakConnection: false,
        facing: 'user',
        hasRemoteVideo: false,
        hasLocalVideo: false,
        elapsed: 0,
        menuOpen: false,
        outputOpen: false,
        outputs: [],
        sinkId: '',
        defaultOutputId: '',
        afterCallId: '',
        afterOtherId: '',
        afterName: '',
        afterAvatar: '',
        afterVideo: false,
        afterDuration: 0,
        ratingStep: 'ask',
        ratingReasons: [],
        autoCloseLeft: 0,
        _autoCloseTimer: null,
        _reportPending: false,
        _timer: null,
        _startedAt: null,
        _unsub: null,
        _mediaUnsubs: [],
        _beforeUnload: null,
        _onDeviceChange: null,
        _focusReturn: null,
        _reportConfirm: null,
        _wakeLock: null,
        _onVisibility: null,

        init() {
            this._unsub = callState.subscribe((s) => this.render(s));
            this._mediaUnsubs.push(callState.on('remote-track', ({ track }) => this.attachRemote(track)));
            this._mediaUnsubs.push(callState.on('local-video-track', ({ track }) => this.attachLocal(track)));
            this._mediaUnsubs.push(callState.on('track-removed', ({ track }) => this.detach(track)));
            this._mediaUnsubs.push(callState.on('ended', (summary) => this.openAfterCall(summary)));
            this.render(callState.snapshot());
        },

        destroy() {
            if (this._unsub) {
                this._unsub();
            }
            this._mediaUnsubs.forEach((fn) => fn && fn());
            this.stopTimer();
            this.stopAutoClose();
            this.unwatchDevices();
            this.removeUnloadGuard();
        },

        render(s) {
            const active = CALL_ACTIVE_STATUSES.includes(s.status);
            const wasActive = CALL_ACTIVE_STATUSES.includes(this.status);
            this.status = s.status;
            this.type = s.type || 'audio';
            this.role = s.role || 'caller';
            this.muted = s.muted;
            this.cameraOff = s.cameraOff;
            this.reconnecting = !!s.reconnecting;
            this.peerCameraOff = !!s.peerCameraOff;
            this.peerMicMuted = !!s.peerMicMuted;
            this.weakConnection = !!s.weakConnection;
            this.facing = s.facing || 'user';
            if (s.other) {
                this.otherId = s.other.id || '';
                this.otherName = s.other.name || s.other.username || '';
                this.otherUsername = s.other.username || '';
                this.otherAvatar = s.other.avatar || '';
            }

            if (active && this.phase === 'after') {
                // A new call takes the surface from the after-call screen, and
                // must get the same setup as a call that opens it.
                this.visible = false;
                this.closeAfterCall();
            }

            if (active && !this.visible) {
                this.rememberFocus();
                this.visible = true;
                this.addUnloadGuard();
                this.watchDevices();
                this.$nextTick(() => {
                    if (!stackedDialogOpen()) {
                        this.$el.focus();
                    }
                });
            } else if (!active && this.visible && this.phase === 'call') {
                this.visible = false;
                this.closeReportConfirm();
            }

            if (!active && wasActive) {
                this.resetCallView();
            }

            if (s.status === 'active') {
                this.holdScreenOn();
            }

            if (s.status === 'active' && !this._timer) {
                this.startTimer(s.startedAt);
                // Device labels need the microphone grant, which a connected call has.
                this.refreshOutputs();
            } else if (s.status !== 'active' && this._timer) {
                this.stopTimer();
            }

            if (active) {
                this.$nextTick(() => lucide(this.$el));
            }
        },

        resetCallView() {
            this.releaseScreen();
            this.removeUnloadGuard();
            this.unwatchDevices();
            this.hasRemoteVideo = false;
            this.hasLocalVideo = false;
            this.elapsed = 0;
            this.menuOpen = false;
            this.outputOpen = false;
            this.outputs = [];
        },

        rememberFocus() {
            const el = pageFocus() || ringFocusReturn;
            ringFocusReturn = null;
            if (el) {
                this._focusReturn = el;
            }
        },

        restoreFocus() {
            const el = this._focusReturn;
            this._focusReturn = null;
            if (!stackedDialogOpen()) {
                focusIfShown(el);
            }
        },

        dialogLabel() {
            if (this.phase === 'after') {
                return this.afterName || this.afterStatusText();
            }
            return this.otherName || this.statusText();
        },

        // --- media attachment ---
        attachRemote(track) {
            if (!track) {
                return;
            }
            if (track.kind === 'video') {
                track.attach(this.$refs.remoteVideo);
                this.hasRemoteVideo = true;
            } else if (track.kind === 'audio') {
                track.attach(this.$refs.remoteAudio);
            }
        },

        attachLocal(track) {
            if (track && this.$refs.localVideo) {
                track.attach(this.$refs.localVideo);
                this.hasLocalVideo = true;
            }
        },

        detach(track) {
            if (!track) {
                return;
            }
            try {
                track.detach();
            } catch (e) {
                /* ignore */
            }
            if (track.kind === 'video') {
                this.hasRemoteVideo = false;
            }
        },

        // --- timer ---
        startTimer(startedAt) {
            this._startedAt = startedAt || Date.now();
            this.tick();
            this._timer = setInterval(() => this.tick(), 500);
        },

        tick() {
            this.elapsed = Math.max(0, Math.round((Date.now() - this._startedAt) / 1000));
        },

        stopTimer() {
            if (this._timer) {
                clearInterval(this._timer);
                this._timer = null;
            }
        },

        timerText() {
            return formatDuration(this.elapsed);
        },

        statusText() {
            if (this.reconnecting) {
                return t('common.calls_reconnecting');
            }
            if (this.status === 'active') {
                return this.timerText();
            }
            if (this.status === 'connecting') {
                return t('common.calls_connecting');
            }
            return t('common.calls_calling');
        },

        // --- screen wake lock ---
        // The browser drops the lock when the tab hides, so it is taken again on return.
        async holdScreenOn() {
            if (this._wakeLock || typeof navigator === 'undefined' || !navigator.wakeLock) {
                return;
            }
            if (!this._onVisibility) {
                this._onVisibility = () => {
                    if (document.visibilityState === 'visible' && this.status === 'active') {
                        this.holdScreenOn();
                    }
                };
                document.addEventListener('visibilitychange', this._onVisibility);
            }
            try {
                const lock = await navigator.wakeLock.request('screen');
                if (this.status !== 'active') {
                    lock.release();
                    return;
                }
                this._wakeLock = lock;
                lock.addEventListener('release', () => {
                    if (this._wakeLock === lock) {
                        this._wakeLock = null;
                    }
                });
            } catch {
                /* denied, or the tab is hidden */
            }
        },

        releaseScreen() {
            if (this._onVisibility) {
                document.removeEventListener('visibilitychange', this._onVisibility);
                this._onVisibility = null;
            }
            if (this._wakeLock) {
                this._wakeLock.release();
                this._wakeLock = null;
            }
        },

        // --- unload guard ---
        addUnloadGuard() {
            if (this._beforeUnload) {
                return;
            }
            this._beforeUnload = (e) => {
                e.preventDefault();
                e.returnValue = '';
                return '';
            };
            window.addEventListener('beforeunload', this._beforeUnload);
        },

        removeUnloadGuard() {
            if (this._beforeUnload) {
                window.removeEventListener('beforeunload', this._beforeUnload);
                this._beforeUnload = null;
            }
        },

        // --- controls ---
        isVideo() {
            return this.type === 'video';
        },

        // A muted camera keeps its last frame, so the peer's avatar replaces it.
        showRemoteVideoStage() {
            return this.phase === 'call' && this.isVideo() && this.status === 'active'
                && this.hasRemoteVideo && !this.peerCameraOff && !this.reconnecting;
        },

        showPeerMuted() {
            return this.phase === 'call' && this.status === 'active' && this.peerMicMuted && !this.reconnecting;
        },

        // One line about the connection or the peer's media, most urgent first, as in the mobile app.
        noticeKind() {
            if (this.phase !== 'call' || this.status !== 'active' || this.reconnecting) {
                return '';
            }
            if (this.weakConnection) {
                return 'weak';
            }
            if (this.peerMicMuted) {
                return 'muted';
            }
            return this.isVideo() && this.peerCameraOff ? 'camera' : '';
        },

        peerMutedLabel() {
            return t('common.calls_peer_muted', { name: shortName(this.otherName) });
        },

        noticeText() {
            const params = { name: shortName(this.otherName) };
            switch (this.noticeKind()) {
                case 'weak':
                    return t('common.calls_weak_connection');
                case 'muted':
                    return this.peerMutedLabel();
                case 'camera':
                    return t('common.calls_peer_camera_off', params);
                default:
                    return '';
            }
        },

        toggleMute() {
            callState.toggleMute();
        },

        toggleCamera() {
            callState.toggleCamera();
        },

        switchCamera() {
            callState.switchCamera();
        },

        canSwitchCamera() {
            return this.isVideo() && callState.canSwitchCamera();
        },

        end() {
            callState.hangUp();
        },

        onEscape() {
            if (stackedDialogOpen()) {
                return;
            }
            if (this.phase === 'after') {
                this.closeAfterCall();
                return;
            }
            if (this.menuOpen) {
                this.menuOpen = false;
                this.$refs.menuButton?.focus();
            }
            if (this.outputOpen) {
                this.outputOpen = false;
                this.$refs.outputButton?.focus();
            }
        },

        // --- output device ---
        // Shown only where the browser can route audio to a chosen device and
        // there is more than one device to choose.
        supportsOutputPick() {
            return typeof HTMLMediaElement !== 'undefined'
                && 'setSinkId' in HTMLMediaElement.prototype
                && !!(navigator.mediaDevices && navigator.mediaDevices.enumerateDevices);
        },

        canPickOutput() {
            return this.outputs.length > 1;
        },

        watchDevices() {
            if (this._onDeviceChange || !this.supportsOutputPick() || !navigator.mediaDevices.addEventListener) {
                return;
            }
            this._onDeviceChange = () => this.refreshOutputs();
            navigator.mediaDevices.addEventListener('devicechange', this._onDeviceChange);
        },

        unwatchDevices() {
            if (this._onDeviceChange) {
                navigator.mediaDevices.removeEventListener('devicechange', this._onDeviceChange);
                this._onDeviceChange = null;
            }
        },

        async refreshOutputs() {
            if (!this.supportsOutputPick()) {
                this.outputs = [];
                return;
            }
            let devices = [];
            try {
                devices = await navigator.mediaDevices.enumerateDevices();
            } catch (e) {
                devices = [];
            }
            if (!CALL_ACTIVE_STATUSES.includes(this.status)) {
                return;
            }
            const all = devices.filter((d) => d.kind === 'audiooutput');
            const alias = all.find((d) => d.deviceId === 'default');
            this.outputs = all
                .filter((d) => d.deviceId && d.label && !OUTPUT_ALIAS_IDS.includes(d.deviceId))
                .map((d) => ({ id: d.deviceId, label: d.label, groupId: d.groupId }));
            const systemDefault = alias && this.outputs.find((o) => o.groupId === alias.groupId);
            this.defaultOutputId = systemDefault ? systemDefault.id : '';
            if (this.sinkId && !this.outputs.some((o) => o.id === this.sinkId)) {
                // The chosen device went away. Follow the system default again.
                this.sinkId = '';
                const el = this.$refs.remoteAudio;
                if (el && typeof el.setSinkId === 'function') {
                    el.setSinkId('').catch(() => {});
                }
            }
            if (!this.canPickOutput()) {
                this.outputOpen = false;
            } else if (this.outputOpen) {
                this.$nextTick(() => lucide(this.$refs.outputMenu));
            }
        },

        selectedOutputId() {
            return this.sinkId || this.defaultOutputId;
        },

        toggleOutputMenu() {
            this.menuOpen = false;
            this.outputOpen = !this.outputOpen;
            if (this.outputOpen) {
                this.refreshOutputs();
                this.$nextTick(() => lucide(this.$refs.outputMenu));
            }
        },

        async selectOutput(id) {
            this.outputOpen = false;
            const el = this.$refs.remoteAudio;
            if (!el || typeof el.setSinkId !== 'function') {
                return;
            }
            try {
                await el.setSinkId(id);
                this.sinkId = id;
            } catch (error) {
                // Firefox allows a non-default output only after its own device prompt.
                if (error && error.name === 'NotAllowedError' && navigator.mediaDevices.selectAudioOutput) {
                    try {
                        const device = await navigator.mediaDevices.selectAudioOutput({ deviceId: id });
                        await el.setSinkId(device.deviceId);
                        this.sinkId = device.deviceId;
                        return;
                    } catch (e) {
                        /* fall through to the error toast */
                    }
                }
                window.showError?.(t('common.calls_error_output'));
            }
        },

        // --- report ---
        toggleMenu() {
            this.outputOpen = false;
            this.menuOpen = !this.menuOpen;
        },

        confirmReport() {
            this.menuOpen = false;
            const id = this.otherId;
            const callId = callState.snapshot().callId || '';
            if (!id || !window.ConfirmModal) {
                return;
            }
            // ConfirmModal keeps a copy of the options, so a token marks this confirm.
            // A string, because Alpine returns a stored object as a proxy.
            const token = `call-report-${Date.now()}-${Math.random()}`;
            const config = {
                title: t('common.calls_report_confirm_title'),
                message: t('common.calls_report_confirm_body', { username: shortName(this.otherName) }),
                confirmText: t('common.calls_report_end'),
                type: 'danger',
                // Above the overlay (z-index 10040 in in-call-overlay.twig).
                zIndex: 10060,
                onConfirm: () => {
                    this._reportConfirm = null;
                    this.reportAndEnd(id, callId);
                },
                onCancel: () => {
                    // A cancel by closeReportConfirm() leaves focus to the call end.
                    if (this._reportConfirm === token) {
                        this._reportConfirm = null;
                        this.$refs.menuButton?.focus();
                    }
                },
            };
            config.owner = token;
            this._reportConfirm = token;
            window.ConfirmModal.show(config);
        },

        // The confirm belongs to one call. It must not stay over the next ring.
        closeReportConfirm() {
            const modal = window.ConfirmModal;
            const token = this._reportConfirm;
            this._reportConfirm = null;
            if (token && modal?.activeModal?.config?.owner === token) {
                modal.close(false);
            }
        },

        async reportAndEnd(id, callId) {
            // The report flow replaces the after-call screen.
            this._reportPending = true;
            try {
                // The confirm can outlive its call. Never end a later call.
                if (callState.isBusy() && String(callState.snapshot().callId || '') === String(callId)) {
                    callState.hangUp();
                }
            } finally {
                this._reportPending = false;
            }
            if (this.phase === 'after') {
                this.closeAfterCall();
            }

            // Reporting is the primary safety action, so open it first. A slow or
            // failing block must never delay or abort the report. Block and revoke
            // run best-effort afterward.
            if (id && window.openReportModal) {
                window.openReportModal('user', id, id);
            }

            if (id) {
                try {
                    await callApi.blockUser(id);
                } catch (e) {
                    window.showError?.(t('common.calls_error_block'));
                }

                try {
                    await callApi.revokeAuthorization(id);
                } catch (e) {
                    // A block is already enough to prevent future calls. Revoke is
                    // best-effort cleanup for the pair authorization row.
                }
            }
        },

        // --- after-call screen ---
        openAfterCall(summary) {
            if (this._reportPending || !hasAfterCallScreen(summary)) {
                if (!this.visible) {
                    this.restoreFocus();
                }
                return;
            }
            this.phase = 'after';
            afterCallOpen = true;
            this.afterCallId = summary.callId ? String(summary.callId) : '';
            this.afterOtherId = summary.other?.id ? String(summary.other.id) : '';
            this.afterName = summary.other?.name || summary.other?.username || '';
            this.afterAvatar = summary.other?.avatar || '';
            this.afterVideo = summary.type === 'video';
            this.afterDuration = summary.duration || 0;
            this.ratingStep = 'ask';
            this.ratingReasons = [];
            this.visible = true;
            this.startAutoClose();
            this.$nextTick(() => {
                lucide(this.$el);
                if (!stackedDialogOpen()) {
                    this.$refs.afterClose?.focus();
                }
            });
        },

        closeAfterCall() {
            this.stopAutoClose();
            this.phase = 'call';
            afterCallOpen = false;
            if (!CALL_ACTIVE_STATUSES.includes(this.status)) {
                this.visible = false;
                this.restoreFocus();
            }
        },

        startAutoClose() {
            this.stopAutoClose();
            this.autoCloseLeft = AFTER_CALL_AUTO_CLOSE_SECONDS;
            this._autoCloseTimer = setInterval(() => {
                this.autoCloseLeft -= 1;
                if (this.autoCloseLeft <= 0) {
                    this.closeAfterCall();
                }
            }, 1000);
        },

        stopAutoClose() {
            if (this._autoCloseTimer) {
                clearInterval(this._autoCloseTimer);
                this._autoCloseTimer = null;
            }
            this.autoCloseLeft = 0;
        },

        // Any pointer or key input on the screen keeps it open.
        touchAfterCall() {
            if (this.phase === 'after') {
                this.stopAutoClose();
            }
        },

        afterStatusText() {
            return t('common.calls_ended_with_duration', { duration: formatDuration(this.afterDuration) });
        },

        autoCloseText() {
            return t('common.calls_rating_autoclose', { seconds: this.autoCloseLeft });
        },

        afterReportText() {
            return t('common.calls_rating_report', { username: shortName(this.afterName) });
        },

        ratingReasonList() {
            return RATING_REASONS.filter((r) => this.afterVideo || !VIDEO_ONLY_REASONS.includes(r));
        },

        reasonLabel(reason) {
            return t(`common.calls_rating_reason_${reason}`);
        },

        rateGood() {
            this.submitRating('good', []);
        },

        rateBad() {
            this.stopAutoClose();
            this.ratingStep = 'reasons';
            this.$nextTick(() => {
                const root = overlayRoot();
                lucide(root);
                root?.querySelector('[data-rating-reasons] input')?.focus();
            });
        },

        sendReasons() {
            this.submitRating('bad', this.ratingReasons.slice());
        },

        submitRating(rating, reasons) {
            const callId = this.afterCallId;
            this.closeAfterCall();
            window.showSuccess?.(t('common.calls_rating_thanks'));
            if (!callId) {
                return;
            }
            // A lost rating is not worth an error the user can act on.
            const warn = (error) => console.warn('calls: call feedback not sent', error);
            const send = () => callApi.sendFeedback(callId, rating, reasons);
            send().catch((error) => {
                // The rating can reach the server before the end of the call does.
                // One retry after the peer-disconnect grace covers that.
                if (error instanceof CallApiError && error.code === 'call_not_connected') {
                    setTimeout(() => send().catch(warn), PEER_DISCONNECT_GRACE_MS + 1000);
                    return;
                }
                warn(error);
            });
        },

        reportFromAfterCall() {
            const id = this.afterOtherId;
            this.closeAfterCall();
            if (id && window.openReportModal) {
                window.openReportModal('user', id, id);
            }
        },
    };
}

export function threadCallButtons() {
    return {
        active: false,
        uid: '',
        username: '',
        name: '',
        avatar: '',
        selfUid: '',
        loading: true,
        callsEnabled: false,
        canCall: false,
        canRequest: false,
        authStatus: 'none',
        requestedBy: null,
        unavailableReason: null,
        unavailableOpen: false,
        _unavailableTrigger: null,
        // 'ask' before a call request goes out, 'sent' while it waits.
        infoSheet: '',
        _infoTrigger: null,
        busy: false,
        _unsubs: [],
        _loadSeq: 0,
        // True once a load for the current contact succeeded.
        _known: false,
        _stripObserver: null,

        init() {
            this.selfUid = document.querySelector('meta[name="user-id"]')?.content || '';

            const onOpen = (e) => {
                // A pending Message Request (#3039) offers no call. The
                // decision bar holding the compose slot is what says so, and
                // accepting the request fires pm:thread-opened again.
                const bottomUi = window.pm_bottom_ui ? window.pm_bottom_ui() : null;
                if (bottomUi && bottomUi.id === 'message-request-bar') {
                    onClose();
                    return;
                }
                this.setContact(e.detail || {});
            };
            const onClose = () => this.clearContact();
            const onAuth = (e) => {
                const from = e.detail && e.detail.userId ? String(e.detail.userId) : null;
                if (this.active && (!from || from === this.uid)) {
                    this.load();
                }
            };
            const onChanged = (e) => {
                const uid = e.detail && e.detail.userId ? String(e.detail.userId) : null;
                if (this.active && (!uid || uid === this.uid)) {
                    this.load();
                }
            };
            document.addEventListener('pm:thread-opened', onOpen);
            document.addEventListener('pm:thread-closed', onClose);
            document.addEventListener('ws:call-auth-accepted', onAuth);
            document.addEventListener('ws:call-auth-request', onAuth);
            document.addEventListener('calls:authorization-changed', onChanged);
            this._unsubs.push(() => document.removeEventListener('pm:thread-opened', onOpen));
            this._unsubs.push(() => document.removeEventListener('pm:thread-closed', onClose));
            this._unsubs.push(() => document.removeEventListener('ws:call-auth-accepted', onAuth));
            this._unsubs.push(() => document.removeEventListener('ws:call-auth-request', onAuth));
            this._unsubs.push(() => document.removeEventListener('calls:authorization-changed', onChanged));

            // The inbound strip overlays the message pane while the header is
            // narrower than @xl, so the pane takes its height as extra top padding.
            if (window.ResizeObserver && this.$refs && this.$refs.inboundStrip) {
                this._stripObserver = new window.ResizeObserver(() => this.syncStripOffset());
                this._stripObserver.observe(this.$refs.inboundStrip);
                this._unsubs.push(() => {
                    this._stripObserver.disconnect();
                    const pane = document.getElementById('threads_right');
                    if (pane) {
                        pane.style.paddingTop = '';
                    }
                });
            }

            // A thread can already be open on initial page load (deep link to
            // /app/messages?thread_id=...), where pm:thread-opened has not fired.
            this.detectActiveThread();
        },

        syncStripOffset() {
            const pane = document.getElementById('threads_right');
            const strip = this.$refs.inboundStrip;
            if (!pane || !strip) {
                return;
            }
            // The header height matches the pane's pt-[56px] in pm/index.twig.
            pane.style.paddingTop = strip.offsetHeight ? `${56 + strip.offsetHeight}px` : '';
        },

        detectActiveThread() {
            const row = document.querySelector('#threads_left .pm_thread.active');
            if (row) {
                this.setContact({
                    uid: row.getAttribute('data-uid') || '',
                    username: row.getAttribute('data-username') || '',
                    name: row.getAttribute('data-name') || '',
                    avatar: row.getAttribute('data-thumb') || '',
                    deleted: row.getAttribute('data-contact-badge-status') === 'deleted',
                });
            }
        },

        destroy() {
            this._unsubs.forEach((fn) => fn && fn());
        },

        // No contact to call. Reset every per-contact field matrix() reads, so
        // it cannot report the previous contact as callable. selfUid is the
        // viewer's own id and stays.
        clearContact() {
            // A load in flight belongs to the contact being dropped, so retire
            // its sequence. Otherwise its reply settles onto a cleared component.
            this._loadSeq++;
            this._known = false;
            this.uid = '';
            this.active = false;
            this.loading = false;
            this.callsEnabled = false;
            this.canCall = false;
            this.canRequest = false;
            this.authStatus = 'none';
            this.requestedBy = null;
            this.unavailableReason = null;
            this.dropUnavailable();
            this.syncRevokeItem();
        },

        // A deleted contact has no user row, so GET /v1/call-authorization
        // answers 404 for them. Treat them as no contact and never ask.
        setContact({ uid, username, name, avatar, deleted }) {
            const nextUid = deleted ? '' : String(uid || '');
            if (!nextUid) {
                this.clearContact();
                return;
            }
            // A deep link opens one contact twice, through detectActiveThread()
            // and pm:thread-opened. Suppress only while in flight: a later open
            // of the same contact must still refresh, a failed load included.
            if (this.active && this.uid === nextUid && this.loading) {
                // The second open carries the fresher metadata, so take it.
                this.username = username || '';
                this.name = name || username || '';
                this.avatar = avatar || '';
                return;
            }
            this.uid = nextUid;
            this.username = username || '';
            this.name = name || username || '';
            this.avatar = avatar || '';
            this.active = true;
            this.loading = true;
            this._known = false;
            this.callsEnabled = false;
            this.canCall = false;
            this.canRequest = false;
            this.authStatus = 'none';
            this.requestedBy = null;
            this.unavailableReason = null;
            this.dropUnavailable();
            this.syncRevokeItem();
            this.load();
        },

        async load() {
            const uid = this.uid;
            if (!uid) {
                this.loading = false;
                return;
            }
            // Open A, open B, open A again leaves two loads in flight for A.
            // The uid matches on both, so only the sequence separates them.
            // A refresh keeps the current controls until it settles.
            const request = ++this._loadSeq;
            try {
                const info = await callApi.getAuthorization(uid);
                if (request !== this._loadSeq) {
                    return;
                }
                this.callsEnabled = info.calls_enabled === true;
                this.canCall = !!info.can_call;
                this.canRequest = !!info.can_request_authorization;
                this.authStatus = info.authorization?.status || 'none';
                this.requestedBy = info.authorization?.requested_by
                    ? String(info.authorization.requested_by)
                    : null;
                this.unavailableReason = typeof info.call_unavailable_reason === 'string'
                    ? info.call_unavailable_reason
                    : null;
                this._known = true;
            } catch (e) {
                if (request !== this._loadSeq) {
                    return;
                }
                // A refresh that fails in transit keeps the last answer. A 4xx is an answer.
                if (this._known && isTransientFailure(e)) {
                    return;
                }
                this.callsEnabled = false;
                this.canCall = false;
                this.canRequest = false;
                this.authStatus = 'none';
                this.requestedBy = null;
                this.unavailableReason = null;
            } finally {
                if (request !== this._loadSeq) {
                    return;
                }
                this.loading = false;
                if (this.matrix() !== 'unavailable') {
                    this.closeUnavailable();
                }
                if (this.infoSheet === 'ask' && this.matrix() !== 'request') {
                    this.closeInfo();
                }
                if (this.infoSheet === 'sent' && this.matrix() !== 'sent') {
                    this.closeInfo();
                }
                this.syncRevokeItem();
                this.$nextTick(() => lucide(this.$el));
            }
        },

        // The revoke control lives in the conversation-header options menu
        // (outside this component), so its visibility is synced imperatively to
        // the authorized state.
        syncRevokeItem() {
            const item = document.getElementById('pm-call-revoke-item');
            if (!item) {
                return;
            }
            const show = this.active && this.authStatus === 'authorized';
            item.classList.toggle('hidden', !show);
            item.classList.toggle('block', show);
        },

        /**
         * The button-matrix state:
         *   inbound  - the other member asked to call me (accept / decline)
         *   call     - fully callable (authorized + every gate passes)
         *   sent     - my request is pending
         *   request  - requestable pending authorization (offer to request)
         *   unavailable - authorized, but a gate denies the call now
         *   hidden   - nothing to show
         */
        matrix() {
            if (this.loading) {
                return 'loading';
            }
            if (!this.callsEnabled) {
                return 'hidden';
            }
            if (this.authStatus === 'requested' && this.requestedBy && this.requestedBy === this.uid) {
                return 'inbound';
            }
            if (this.canCall) {
                return 'call';
            }
            if (this.authStatus === 'requested' && this.requestedBy === this.selfUid) {
                return 'sent';
            }
            if (this.authStatus === 'authorized') {
                return 'unavailable';
            }
            // can_call=false covers many denials a request cannot cure (the
            // other side not in the rollout, ACL, do-not-disturb, the age
            // band). Only offer "request to call" when the server says a
            // request would actually be accepted.
            return this.canRequest ? 'request' : 'hidden';
        },

        inboundPrompt() {
            return t('common.calls_inbound_prompt', { username: shortName(this.name || this.username) });
        },

        askLabel() {
            return t('common.calls_ask_to_call', { username: shortName(this.name || this.username) });
        },

        openUnavailable(trigger) {
            if (followWebPhoneGateRefusal({ error: this.unavailableReason })) {
                return;
            }
            // Safari does not focus a clicked button, so the click passes its target.
            this._unavailableTrigger = trigger || document.activeElement;
            this.unavailableOpen = true;
            this.$nextTick(() => document.getElementById('pm-calls-unavailable-ok')?.focus());
        },

        // The sheet macro calls this on every Escape on the page, open or not.
        closeUnavailable() {
            if (!this.unavailableOpen) {
                return;
            }
            this.unavailableOpen = false;
            const trigger = this._unavailableTrigger;
            this._unavailableTrigger = null;
            focusIfShown(trigger);
        },

        unavailableMessage() {
            // With no reason from the server, name the person and nothing else.
            // A block by the other person reads as call_authorization_required,
            // which an authorized pair cannot act on.
            const reason = this.unavailableReason && this.unavailableReason !== 'call_authorization_required'
                ? this.unavailableReason
                : 'call_recipient_unavailable';
            return callErrorMessage(reason, this.name || this.username);
        },

        unavailableDetail() {
            const code = this.unavailableReason || 'call_recipient_unavailable';
            if (SERVICE_UNAVAILABLE_CODES.includes(code)) {
                return '';
            }
            const key = `common.calls_unavailable_reason_${code}`;
            const translated = t(key);
            return translated === key ? '' : translated;
        },

        // The contact changed, so the old trigger is no place to return focus.
        dropUnavailable() {
            this.unavailableOpen = false;
            this._unavailableTrigger = null;
            this.infoSheet = '';
            this._infoTrigger = null;
        },

        openAsk(trigger) {
            this.openInfo('ask', trigger, 'pm-calls-ask-send');
        },

        openSent(trigger) {
            this.openInfo('sent', trigger, 'pm-calls-sent-ok');
        },

        openInfo(sheet, trigger, focusId) {
            this._infoTrigger = trigger || document.activeElement;
            this.infoSheet = sheet;
            this.$nextTick(() => document.getElementById(focusId)?.focus());
        },

        // The sheet macro calls this on every Escape on the page, open or not.
        closeInfo() {
            if (!this.infoSheet) {
                return;
            }
            this.infoSheet = '';
            const trigger = this._infoTrigger;
            this._infoTrigger = null;
            focusIfShown(trigger);
        },

        askBody() {
            return t('common.calls_ask_body', { username: shortName(this.name || this.username) });
        },

        sentBody() {
            return t('common.calls_sent_body', { username: shortName(this.name || this.username) });
        },

        async sendAsk() {
            // The request button gives way to the sent pill, so focus follows the pill.
            this._infoTrigger = null;
            this.infoSheet = '';
            await this.requestCall();
            this.$nextTick(() => {
                if (this.matrix() === 'sent') {
                    this.$refs.sentButton?.focus();
                }
            });
        },

        startAudio() {
            this.start('audio');
        },

        startVideo() {
            this.start('video');
        },

        async start(type) {
            if (this.busy || !window.InterpalsCalls) {
                return;
            }
            this.busy = true;
            try {
                await window.InterpalsCalls.startCall(this.uid, type, {
                    id: this.uid,
                    username: this.username,
                    name: this.name,
                    avatar: this.avatar,
                });
            } catch (error) {
                this.handleError(error);
            } finally {
                this.busy = false;
            }
        },

        async requestCall() {
            if (this.busy) {
                return;
            }
            this.busy = true;
            // Snapshot the contact: a thread switch during the awaits below
            // re-points this component, and the response state / toast name
            // must stay bound to the contact the user actually asked for.
            const uid = this.uid;
            const name = this.name;
            try {
                // Apply the state the server actually reached, not an assumed
                // 'requested': mutual interest (the other side had an open ask
                // to us) authorizes the pair on this POST.
                const response = await callApi.requestAuthorization(uid);
                const auth = response && response.authorization;
                const status = auth && auth.status;
                if (status === 'authorized') {
                    if (this.uid === uid) {
                        // can_call still needs the server verdict (ACL, DND, ...).
                        await this.load();
                    }
                    window.showSuccess?.(t('common.calls_toast_auth_accepted', { username: shortName(name) }));
                } else if (status === 'revoked' || status === 'declined') {
                    // The pair sits in a state a stale view of this thread did
                    // not know about (e.g. a still-cooling withdrawal from
                    // another tab): the POST changed nothing, so claiming a
                    // request was sent would be false. Apply and re-fetch the
                    // affordances instead.
                    if (this.uid === uid) {
                        this.authStatus = status;
                        this.requestedBy = auth.requested_by ? String(auth.requested_by) : null;
                        await this.load();
                    }
                } else {
                    if (this.uid === uid) {
                        this.authStatus = status || 'requested';
                        this.requestedBy = (auth && auth.requested_by) || this.selfUid;
                    }
                    window.showSuccess?.(t('common.calls_toast_request_sent'));
                }
            } catch (error) {
                this.handleError(error);
            } finally {
                this.busy = false;
                this.$nextTick(() => lucide(this.$el));
            }
        },

        async acceptInbound() {
            if (this.busy) {
                return;
            }
            this.busy = true;
            try {
                await callApi.acceptAuthorization(this.uid);
                // Authorization is necessary but not sufficient for can_call
                // (ACL, DND, age band, rollout are all evaluated server-side),
                // so re-fetch instead of optimistically flipping to callable.
                await this.load();
            } catch (error) {
                this.handleError(error);
            } finally {
                this.busy = false;
                this.$nextTick(() => lucide(this.$el));
            }
        },

        async declineInbound() {
            if (this.busy) {
                return;
            }
            this.busy = true;
            try {
                await callApi.declineAuthorization(this.uid);
                this.authStatus = 'none';
                this.requestedBy = null;
            } catch (error) {
                this.handleError(error);
            } finally {
                this.busy = false;
                this.$nextTick(() => lucide(this.$el));
            }
        },

        handleError(error) {
            // A server or client failure is not a denial, so it takes the generic sentence.
            const denial = error instanceof CallApiError
                && ((error.status >= 400 && error.status < 500) || SERVICE_UNAVAILABLE_CODES.includes(error.code));
            const code = denial ? error.code : '';

            // No calls_error_* copy names the phone gate, so the fallback below
            // would answer it with "unavailable" and no route. The verify page
            // is the route.
            if (followWebPhoneGateRefusal({ error: code })) return;

            const retryAfter = Number(error?.data?.extra?.retry_after) || 0;
            window.showError?.(callErrorMessage(code, this.name || this.username, retryAfter));

            // Codes that mean the affordance is now stale: re-fetch so the button
            // reflects the true state (authorization revoked, target busy, ...).
            if (code === 'call_authorization_required' || code === 'call_busy' || code === 'call_invalid_state'
                || SERVICE_UNAVAILABLE_CODES.includes(code)) {
                this.load();
            }
        },
    };
}

export default { incomingCallModal, inCallOverlay, threadCallButtons };
